import { useState, useEffect, useCallback, useMemo, useRef } from "react";

import { useNavigate } from "react-router-dom";
import { Search, LayoutPanelLeft, Plus } from "lucide-react";
import toast from "react-hot-toast";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SkillCard } from "@/components/skills-marketplace/skill-card";
import { CategoryFilter } from "@/components/skills-marketplace/category-filter";
import { cn } from "@/lib/utils";
import { skillsMarketplaceApi } from "@/lib/api/skills-marketplace";
import type {
  SkillListItem,
  SkillsCategoryCount,
  SkillsPageInfo,
  CustomSkillItem,
} from "@optidev/shared-types";

export default function SkillsMarketplace() {
  const navigate = useNavigate();
  const stickyBarRef = useRef<HTMLDivElement>(null);

  const [skills, setSkills] = useState<SkillListItem[]>([]);
  const [categories, setCategories] = useState<SkillsCategoryCount[]>([]);
  const [installedIds, setInstalledIds] = useState<Set<string>>(new Set());
  const [pageInfo, setPageInfo] = useState<SkillsPageInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingSkillId, setLoadingSkillId] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [customSkills, setCustomSkills] = useState<CustomSkillItem[]>([]);
  const [deletingCustomId, setDeletingCustomId] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Sticky bar scroll detection
  useEffect(() => {
    const mainContainer = document.querySelector("main");
    if (!mainContainer) return;

    const handleScroll = (): void => {
      setIsScrolled(mainContainer.scrollTop > 80);
    };

    mainContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => mainContainer.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch catalog
  const fetchCatalog = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await skillsMarketplaceApi.getCatalog({
        search: search || undefined,
        category: selectedCategory || undefined,
        page,
        limit: 24,
      });
      setSkills(result.items);
      setPageInfo(result.pageInfo);
    } catch (err) {
      console.error("Failed to fetch catalog:", err);
    } finally {
      setIsLoading(false);
    }
  }, [search, selectedCategory, page]);

  // Fetch categories, installed, and custom skills on mount
  useEffect(() => {
    skillsMarketplaceApi
      .getCategories()
      .then(setCategories)
      .catch(console.error);
    skillsMarketplaceApi
      .getInstalled()
      .then((installed) => {
        setInstalledIds(new Set(installed.map((s) => s.id)));
      })
      .catch(console.error);
    skillsMarketplaceApi
      .getCustomSkills()
      .then(setCustomSkills)
      .catch(console.error);
  }, []);

  // Fetch catalog on filter change
  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [searchInput, selectedCategory]);

  // Scroll to top on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      const mainContainer = document.querySelector("main");
      if (mainContainer) mainContainer.scrollTop = 0;
      window.scrollTo(0, 0);
    });
  }, []);

  const handleInstall = useCallback(async (skillId: string) => {
    setLoadingSkillId(skillId);
    try {
      await skillsMarketplaceApi.install(skillId);
      setInstalledIds((prev) => new Set([...prev, skillId]));
      toast.success("Skill installed");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to install";
      toast.error(message);
    } finally {
      setLoadingSkillId(null);
    }
  }, []);

  const handleUninstall = useCallback(async (skillId: string) => {
    setLoadingSkillId(skillId);
    try {
      await skillsMarketplaceApi.uninstall(skillId);
      setInstalledIds((prev) => {
        const next = new Set(prev);
        next.delete(skillId);
        return next;
      });
      toast.success("Skill uninstalled");
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to uninstall";
      toast.error(message);
    } finally {
      setLoadingSkillId(null);
    }
  }, []);

  const handleDeleteCustom = useCallback(async (skillId: string) => {
    setDeletingCustomId(skillId);
    try {
      await skillsMarketplaceApi.deleteCustomSkill(skillId);
      setCustomSkills((prev) => prev.filter((s) => s.id !== skillId));
      toast.success("Skill deleted");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to delete skill";
      toast.error(message);
    } finally {
      setDeletingCustomId(null);
    }
  }, []);

  const totalCount = useMemo(() => {
    return categories.reduce((sum, c) => sum + c.count, 0);
  }, [categories]);

  const isCustomFilter = selectedCategory === "custom";

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-2">
            Skills Marketplace
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Browse and install skills to supercharge your AI agent
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0 mt-1">
          <div className="relative w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search skills..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/dashboard/skills/custom/new")}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Create
          </Button>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="relative mb-4 sm:hidden">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search skills..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-10"
        />
      </div>

      {/* Sticky filter bar */}
      <div
        ref={stickyBarRef}
        className={cn(
          "sticky top-0 z-30 py-4",
          "transition-[border-color,box-shadow,background-color,backdrop-filter] duration-200",
          isScrolled
            ? "border-b border-border shadow-sm bg-background/90 backdrop-blur-sm"
            : "border-b border-transparent bg-transparent",
        )}
      >
        <CategoryFilter
          categories={categories}
          selected={selectedCategory}
          onSelect={setSelectedCategory}
          totalCount={totalCount}
          customCount={customSkills.length}
        />
      </div>

      {/* Unified grid */}
      <div className="mt-6">
        {isCustomFilter ? (
          /* Custom skills only */
          customSkills.length === 0 ? (
            <div className="text-center py-20">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted mx-auto mb-4">
                <LayoutPanelLeft className="h-6 w-6 text-muted-foreground/50" />
              </div>
              <p className="text-foreground font-medium">No custom skills yet</p>
              <p className="text-muted-foreground text-sm mt-1 mb-4">
                Create your own skill to customize your AI agent&apos;s behavior
              </p>
              <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/skills/custom/new")}>
                <Plus className="h-4 w-4 mr-1.5" />
                Create Custom Skill
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {customSkills.map((skill) => (
                <SkillCard
                  key={skill.id}
                  skill={{ id: skill.id, name: skill.name, description: skill.description, author: "you", category: skill.category, icon: skill.icon, tags: [], isFeatured: false, isPopular: false, installCount: 0, installCountExternal: 0, version: "1.0.0" }}
                  isInstalled={false}
                  isLoading={deletingCustomId === skill.id}
                  onInstall={() => {}}
                  onUninstall={() => {}}
                  onClick={() => navigate(`/dashboard/skills/custom/${skill.id}`)}
                  isCustom
                  onEdit={() => navigate(`/dashboard/skills/custom/${skill.id}/edit`)}
                  onDelete={() => handleDeleteCustom(skill.id)}
                />
              ))}
            </div>
          )
        ) : (
          /* Single unified grid: custom cards first, then marketplace */
          isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-[180px] rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {selectedCategory === null && customSkills.map((skill) => (
                <SkillCard
                  key={`custom-${skill.id}`}
                  skill={{ id: skill.id, name: skill.name, description: skill.description, author: "you", category: skill.category, icon: skill.icon, tags: [], isFeatured: false, isPopular: false, installCount: 0, installCountExternal: 0, version: "1.0.0" }}
                  isInstalled={false}
                  isLoading={deletingCustomId === skill.id}
                  onInstall={() => {}}
                  onUninstall={() => {}}
                  onClick={() => navigate(`/dashboard/skills/custom/${skill.id}`)}
                  isCustom
                  onEdit={() => navigate(`/dashboard/skills/custom/${skill.id}/edit`)}
                  onDelete={() => handleDeleteCustom(skill.id)}
                />
              ))}
              {skills.length === 0 && selectedCategory !== null ? (
                <div className="col-span-3 text-center py-20">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted mx-auto mb-4">
                    <LayoutPanelLeft className="h-6 w-6 text-muted-foreground/50" />
                  </div>
                  <p className="text-foreground font-medium">No skills found</p>
                  <p className="text-muted-foreground text-sm mt-1">Try a different search or category</p>
                </div>
              ) : skills.map((skill) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  isInstalled={installedIds.has(skill.id)}
                  isLoading={loadingSkillId === skill.id}
                  onInstall={() => handleInstall(skill.id)}
                  onUninstall={() => handleUninstall(skill.id)}
                  onClick={() => navigate(`/dashboard/skills/${skill.id}`)}
                />
              ))}
            </div>
          )
        )}
      </div>

      {/* Pagination */}
      {pageInfo && pageInfo.totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <Button
            variant="outline"
            size="default"
            disabled={!pageInfo.hasPreviousPage}
            onClick={() => setPage((p) => p - 1)}
            className="h-10 sm:h-9 px-4 sm:px-3"
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            {pageInfo.page} / {pageInfo.totalPages}
          </span>
          <Button
            variant="outline"
            size="default"
            disabled={!pageInfo.hasNextPage}
            onClick={() => setPage((p) => p + 1)}
            className="h-10 sm:h-9 px-4 sm:px-3"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
